"use server";

import {
  addIdentificationEnvSivSchema,
  editIdentificationEnvSivSchema,
  deleteIdentificationEnvSivSchema,
} from "@/lib/schemas/identification-env-siv";

import { validateParams } from "@/lib/utils";
import { prisma } from "@/prisma";
import z from "zod";

import { requireServerRole } from "@/lib/guards";
import { GRP_ROLE_ADMIN } from "@/lib/grp";

import { ActionResult } from "@/types/server-action";
import { v4 as uuidv4 } from "uuid";

export async function addIdentificationEnvSiv(
  params: z.infer<typeof addIdentificationEnvSivSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const {
      environment,
      sivIdentifier,
      marqueJnl,
      codeConcentrateurJnl,
      codePartenaireJnl,
      isActive,
    } = validateParams(addIdentificationEnvSivSchema, params);

    const item = await prisma.identificationEnvSiv.create({
      data: {
        environment,
        sivIdentifier,

        marqueJnl,
        codeConcentrateurJnl,
        codePartenaireJnl,

        isActive,
        versionGroupId: uuidv4(),
        versionNumber: 1,
      },
    });

    return { success: true, data: item };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

export async function editIdentificationEnvSiv(
  params: z.infer<typeof editIdentificationEnvSivSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const {
      id,
      environment,
      sivIdentifier,
      marqueJnl,
      codeConcentrateurJnl,
      codePartenaireJnl,
      isActive = true,
    } = validateParams(editIdentificationEnvSivSchema, params);

    const current = await prisma.identificationEnvSiv.findUnique({
      where: { id },
    });

    if (!current) {
      return {
        success: false,
        message: "Paramètre introuvable",
      };
    }

    const newVersion = await prisma.$transaction(async (tx) => {
      await tx.identificationEnvSiv.update({
        where: { id },
        data: { isActive: false },
      });
      return tx.identificationEnvSiv.create({
        data: {
          environment,
          sivIdentifier,

          marqueJnl,
          codeConcentrateurJnl,
          codePartenaireJnl,

          isActive,
          versionGroupId: current.versionGroupId,
          versionNumber: current.versionNumber + 1,
        },
      });
    });

    return { success: true, data: newVersion };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

export async function deleteIdentificationEnvSiv(
  params: z.infer<typeof deleteIdentificationEnvSivSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const { id } = validateParams(deleteIdentificationEnvSivSchema, params);

    const record = await prisma.identificationEnvSiv.findUnique({
      where: { id },
    });

    if (!record) {
      return {
        success: false,
        message: "Enregistrement introuvable.",
      };
    }

    await prisma.identificationEnvSiv.updateMany({
      where: {
        versionGroupId: record.versionGroupId,
      },
      data: {
        deletedAt: new Date(),
        isActive: false,
      },
    });

    return { success: true };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
